/**
 * ModelEditorComponent - Enhanced 3D model editing capabilities for the editor
 *
 * Provides:
 * - Transform gizmos (position, rotation, scale) using individual gizmos for Node support
 * - Human scale reference capsule (1.7m) for VR scale visualization
 * - Skeleton viewer for bone visualization on rigged models
 * - Mesh inspection and visibility control
 * - Animation playback controls with timeline scrubbing
 * - Model statistics (vertex count, face count, etc.)
 */

import { Scene } from '@babylonjs/core/scene';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Node } from '@babylonjs/core/node';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { PositionGizmo } from '@babylonjs/core/Gizmos/positionGizmo';
import { RotationGizmo } from '@babylonjs/core/Gizmos/rotationGizmo';
import { ScaleGizmo } from '@babylonjs/core/Gizmos/scaleGizmo';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { SkeletonViewer } from '@babylonjs/core/Debug/skeletonViewer';
import { Skeleton } from '@babylonjs/core/Bones/skeleton';
import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import { Logger } from '../utils/Logger';

// Side-effect imports for gizmos
import '@babylonjs/core/Gizmos/rotationGizmo';
import '@babylonjs/core/Gizmos/positionGizmo';
import '@babylonjs/core/Gizmos/scaleGizmo';

const logger = Logger.getLogger('ModelEditorComponent');

/**
 * Information about a mesh in the model
 */
export interface MeshInfo {
  name: string;
  id: string;
  vertexCount: number;
  faceCount: number;
  isVisible: boolean;
  hasSkeleton: boolean;
  materialName: string | null;
}

/**
 * Statistics about a loaded model
 */
export interface ModelStats {
  totalMeshes: number;
  totalVertices: number;
  totalFaces: number;
  hasSkeleton: boolean;
  boneCount: number;
  animationCount: number;
  boundingBoxSize: { x: number; y: number; z: number };
}

/**
 * Information about an animation
 */
export interface AnimationInfo {
  name: string;
  frameCount: number;
  duration: number;
  from: number;
  to: number;
  targetedAnimations: string[];
}

/**
 * Current animation playback state
 */
export interface AnimationState {
  isPlaying: boolean;
  currentAnimation: string | null;
  currentFrame: number;
  totalFrames: number;
  speed: number;
  loop: boolean;
}

/**
 * Transform data emitted when gizmo transform ends
 */
export interface TransformData {
  position: { x: number; y: number; z: number };
  rotation: { x: number; y: number; z: number };
  scale: { x: number; y: number; z: number };
}

type GizmoMode = 'position' | 'rotation' | 'scale' | 'boundingBox' | 'none';

export class ModelEditorComponent {
  private scene: Scene;
  private utilityLayer: UtilityLayerRenderer | null = null;
  private positionGizmo: PositionGizmo | null = null;
  private rotationGizmo: RotationGizmo | null = null;
  private scaleGizmo: ScaleGizmo | null = null;
  private skeletonViewers: Map<string, SkeletonViewer> = new Map();
  private humanScaleRef: Mesh | null = null;
  private currentGizmoMode: GizmoMode = 'none';
  private attachedNode: Node | null = null; // Can be TransformNode or AbstractMesh

  // Animation state
  private currentAnimationGroup: AnimationGroup | null = null;
  private animationSpeed: number = 1.0;
  private animationLoop: boolean = true;
  private frameUpdateInterval: ReturnType<typeof setInterval> | null = null;

  // Callbacks
  public onGizmoTransformEnd?: (transform: TransformData) => void;
  public onHumanScalePositionChanged?: (position: { x: number; y: number; z: number }) => void;
  public onAnimationFrameUpdate?: (currentFrame: number, totalFrames: number) => void;

  constructor(scene: Scene) {
    this.scene = scene;
    logger.debug('ModelEditorComponent initialized');
  }

  // =========================================================================
  // GIZMO MANAGEMENT
  // =========================================================================

  /**
   * Initialize individual gizmos (called lazily when first needed)
   * We use individual gizmos instead of GizmoManager because:
   * - GizmoManager.attachToMesh only accepts AbstractMesh
   * - Individual gizmos support attachedNode which accepts any Node (including TransformNode)
   * - This allows us to attach gizmos to the model's root TransformNode
   */
  private initializeGizmos(): void {
    if (this.positionGizmo) return; // Already initialized

    // Create utility layer for gizmo rendering
    this.utilityLayer = new UtilityLayerRenderer(this.scene);

    // Create individual gizmos
    this.positionGizmo = new PositionGizmo(this.utilityLayer);
    this.rotationGizmo = new RotationGizmo(this.utilityLayer);
    this.scaleGizmo = new ScaleGizmo(this.utilityLayer);

    // Configure gizmos
    this.positionGizmo.updateGizmoRotationToMatchAttachedMesh = false;
    this.positionGizmo.scaleRatio = 1.2;

    this.rotationGizmo.updateGizmoRotationToMatchAttachedMesh = true;
    this.rotationGizmo.scaleRatio = 1.2;

    this.scaleGizmo.sensitivity = 3;
    this.scaleGizmo.scaleRatio = 1.2;

    // Setup callbacks for transform end
    this.setupGizmoCallbacks();

    logger.debug('Individual gizmos initialized');
  }

  /**
   * Enable gizmos with the specified mode
   */
  enableGizmos(mode: GizmoMode): void {
    if (mode === 'none') {
      this.disableGizmos();
      return;
    }

    // Initialize gizmos if needed
    this.initializeGizmos();

    this.currentGizmoMode = mode;

    // Hide all gizmos first by detaching
    if (this.positionGizmo) this.positionGizmo.attachedNode = null;
    if (this.rotationGizmo) this.rotationGizmo.attachedNode = null;
    if (this.scaleGizmo) this.scaleGizmo.attachedNode = null;

    // Enable and attach the requested mode
    if (this.attachedNode) {
      switch (mode) {
        case 'position':
          if (this.positionGizmo) this.positionGizmo.attachedNode = this.attachedNode;
          break;
        case 'rotation':
          if (this.rotationGizmo) this.rotationGizmo.attachedNode = this.attachedNode;
          break;
        case 'scale':
          if (this.scaleGizmo) this.scaleGizmo.attachedNode = this.attachedNode;
          break;
        case 'boundingBox':
          // For bounding box, we'll use scale gizmo as fallback
          if (this.scaleGizmo) this.scaleGizmo.attachedNode = this.attachedNode;
          break;
      }
    }

    logger.debug(`Gizmo mode enabled: ${mode}`);
  }

  /**
   * Setup callbacks for when gizmo transforms end
   */
  private setupGizmoCallbacks(): void {
    console.log('[ModelEditorComponent] Setting up gizmo callbacks');

    // Position gizmo drag end
    if (this.positionGizmo) {
      this.positionGizmo.onDragEndObservable.add(() => {
        console.log('[ModelEditorComponent] Position gizmo drag ended');
        this.emitTransformEnd();
      });
    }

    // Rotation gizmo drag end
    if (this.rotationGizmo) {
      this.rotationGizmo.onDragEndObservable.add(() => {
        console.log('[ModelEditorComponent] Rotation gizmo drag ended');
        this.emitTransformEnd();
      });
    }

    // Scale gizmo drag end
    if (this.scaleGizmo) {
      this.scaleGizmo.onDragEndObservable.add(() => {
        console.log('[ModelEditorComponent] Scale gizmo drag ended');
        this.emitTransformEnd();
      });
    }
  }

  /**
   * Emit transform data when gizmo manipulation ends
   * Reads from the attached node (TransformNode or AbstractMesh)
   */
  private emitTransformEnd(): void {
    console.log('[ModelEditorComponent] emitTransformEnd called, attachedNode:', !!this.attachedNode, 'callback:', !!this.onGizmoTransformEnd);

    if (!this.attachedNode) {
      console.warn('[ModelEditorComponent] No attached node - cannot emit transform');
      return;
    }

    if (!this.onGizmoTransformEnd) {
      console.warn('[ModelEditorComponent] No onGizmoTransformEnd callback set');
      return;
    }

    // TransformNode and AbstractMesh both have position, rotation, scaling properties
    const node = this.attachedNode as TransformNode;

    const transform: TransformData = {
      position: {
        x: node.position.x,
        y: node.position.y,
        z: node.position.z,
      },
      rotation: {
        x: node.rotation.x,
        y: node.rotation.y,
        z: node.rotation.z,
      },
      scale: {
        x: node.scaling.x,
        y: node.scaling.y,
        z: node.scaling.z,
      },
    };

    console.log('[ModelEditorComponent] Emitting transform:', transform);
    this.onGizmoTransformEnd(transform);
    logger.debug('Transform end:', transform);
  }

  /**
   * Disable all gizmos
   */
  disableGizmos(): void {
    if (this.positionGizmo) this.positionGizmo.attachedNode = null;
    if (this.rotationGizmo) this.rotationGizmo.attachedNode = null;
    if (this.scaleGizmo) this.scaleGizmo.attachedNode = null;

    this.currentGizmoMode = 'none';
    logger.debug('Gizmos disabled');
  }

  /**
   * Attach gizmo to a specific node (TransformNode or AbstractMesh)
   * This allows attaching to model root nodes which are TransformNodes
   */
  attachGizmoToNode(node: Node | null): void {
    this.attachedNode = node;

    // Re-enable current gizmo mode with new node
    if (node && this.currentGizmoMode !== 'none') {
      this.enableGizmos(this.currentGizmoMode);
    } else {
      this.disableGizmos();
    }

    logger.debug('Gizmo attached to:', node?.name || 'null');
  }

  /**
   * Attach gizmo to a specific mesh (legacy method, calls attachGizmoToNode)
   * @deprecated Use attachGizmoToNode instead
   */
  attachGizmoToMesh(mesh: AbstractMesh | null): void {
    this.attachGizmoToNode(mesh);
  }

  /**
   * Get current gizmo mode
   */
  getGizmoMode(): GizmoMode {
    return this.currentGizmoMode;
  }

  // =========================================================================
  // HUMAN SCALE REFERENCE
  // =========================================================================

  /**
   * Create the human scale reference model
   * A 1.7m tall semi-transparent capsule representing average human height
   */
  createHumanScaleReference(): Mesh {
    if (this.humanScaleRef) {
      return this.humanScaleRef;
    }

    // Create capsule (1.7m tall, ~0.4m diameter)
    // BabylonJS CreateCapsule height includes the caps, so we adjust
    this.humanScaleRef = MeshBuilder.CreateCapsule('humanScaleReference', {
      height: 1.7,
      radius: 0.2,
      tessellation: 16,
      subdivisions: 4,
    }, this.scene);

    // Position at origin, center at eye level (roughly 0.85m up)
    this.humanScaleRef.position.y = 0.85;

    // Create semi-transparent material
    const material = new StandardMaterial('humanScaleMaterial', this.scene);
    material.diffuseColor = new Color3(0.2, 0.6, 1.0); // Light blue
    material.emissiveColor = new Color3(0.1, 0.3, 0.5); // Subtle glow
    material.alpha = 0.4;
    material.backFaceCulling = false;

    this.humanScaleRef.material = material;
    this.humanScaleRef.isPickable = true;

    // Store metadata for identification
    this.humanScaleRef.metadata = { isHumanScaleReference: true };

    // Make it render above most things but below UI
    this.humanScaleRef.renderingGroupId = 1;

    logger.debug('Human scale reference created');
    return this.humanScaleRef;
  }

  /**
   * Show the human scale reference
   */
  showHumanScaleReference(): void {
    if (!this.humanScaleRef) {
      this.createHumanScaleReference();
    }
    if (this.humanScaleRef) {
      this.humanScaleRef.setEnabled(true);
      this.humanScaleRef.isVisible = true;
    }
  }

  /**
   * Hide the human scale reference
   */
  hideHumanScaleReference(): void {
    if (this.humanScaleRef) {
      this.humanScaleRef.setEnabled(false);
    }
  }

  /**
   * Get the human scale reference mesh
   */
  getHumanScaleReference(): Mesh | null {
    return this.humanScaleRef;
  }

  /**
   * Set the position of the human scale reference
   * Y position is offset to keep feet at the specified height
   */
  setHumanScalePosition(position: { x: number; y: number; z: number }): void {
    if (!this.humanScaleRef) {
      this.createHumanScaleReference();
    }
    if (this.humanScaleRef) {
      // Position.y is the center, so add 0.85 to put feet at the specified y
      this.humanScaleRef.position.set(position.x, position.y + 0.85, position.z);

      if (this.onHumanScalePositionChanged) {
        this.onHumanScalePositionChanged(position);
      }
    }
  }

  /**
   * Get the current human scale reference position (feet position)
   */
  getHumanScalePosition(): { x: number; y: number; z: number } | null {
    if (!this.humanScaleRef) return null;

    return {
      x: this.humanScaleRef.position.x,
      y: this.humanScaleRef.position.y - 0.85, // Feet position
      z: this.humanScaleRef.position.z,
    };
  }

  /**
   * Set the rotation of the human scale reference (Y-axis only for spawn direction)
   * @param rotationY - Rotation in radians around Y axis
   */
  setHumanScaleRotation(rotationY: number): void {
    if (!this.humanScaleRef) {
      this.createHumanScaleReference();
    }
    if (this.humanScaleRef) {
      this.humanScaleRef.rotation.y = rotationY;

      if (this.onHumanScaleRotationChanged) {
        this.onHumanScaleRotationChanged(rotationY);
      }
    }
  }

  /**
   * Get the current human scale rotation (Y-axis)
   */
  getHumanScaleRotation(): number {
    if (!this.humanScaleRef) return 0;
    return this.humanScaleRef.rotation.y;
  }

  // Callback for rotation changes
  public onHumanScaleRotationChanged?: (rotationY: number) => void;

  // Track if human scale gizmo is active (for Y-locking)
  private humanScaleGizmoActive: boolean = false;

  /**
   * Attach position gizmo to human scale reference
   * Enables position gizmo with Y-axis locked to 0
   */
  attachGizmoToHumanScale(): void {
    if (!this.humanScaleRef) {
      this.createHumanScaleReference();
    }
    if (!this.humanScaleRef) return;

    // Enable position gizmo mode
    this.enableGizmos('position');

    // Attach to human scale
    this.attachedNode = this.humanScaleRef;
    if (this.positionGizmo) {
      this.positionGizmo.attachedNode = this.humanScaleRef;

      // Lock Y axis by making it invisible/disabled
      // The Y gizmo is the green one (index 1 in the gizmo arrows)
      this.positionGizmo.yGizmo.isEnabled = false;
    }

    this.humanScaleGizmoActive = true;

    // Add observable to enforce Y=0.85 (center of capsule, feet at y=0)
    this.humanScaleRef.onAfterWorldMatrixUpdateObservable.add(() => {
      if (this.humanScaleGizmoActive && this.humanScaleRef) {
        // Lock Y position (capsule center should be at 0.85 to put feet at 0)
        if (Math.abs(this.humanScaleRef.position.y - 0.85) > 0.001) {
          this.humanScaleRef.position.y = 0.85;
        }

        // Update VR spawn position callback
        if (this.onHumanScalePositionChanged) {
          this.onHumanScalePositionChanged({
            x: this.humanScaleRef.position.x,
            y: 0, // Feet always at y=0
            z: this.humanScaleRef.position.z,
          });
        }
      }
    });

    logger.debug('Gizmo attached to human scale reference with Y locked');
  }

  /**
   * Detach gizmo from human scale reference
   */
  detachGizmoFromHumanScale(): void {
    this.humanScaleGizmoActive = false;

    // Clear the observable
    if (this.humanScaleRef) {
      this.humanScaleRef.onAfterWorldMatrixUpdateObservable.clear();
    }

    // Re-enable Y gizmo for other uses
    if (this.positionGizmo) {
      this.positionGizmo.yGizmo.isEnabled = true;
    }

    this.disableGizmos();
    logger.debug('Gizmo detached from human scale reference');
  }

  // =========================================================================
  // GROUND PLANE
  // =========================================================================

  private groundPlane: Mesh | null = null;

  /**
   * Create and show a ground plane for spatial reference
   * Shows a grid pattern to help visualize the floor level
   */
  showGroundPlane(size: number = 10, gridDivisions: number = 10): void {
    if (this.groundPlane) {
      this.groundPlane.setEnabled(true);
      return;
    }

    // Create ground mesh
    this.groundPlane = MeshBuilder.CreateGround('groundPlane', {
      width: size,
      height: size,
      subdivisions: 1,
    }, this.scene);

    // Position at y=0 (floor level)
    this.groundPlane.position.y = 0;

    // Create grid material
    const material = new StandardMaterial('groundPlaneMaterial', this.scene);
    material.diffuseColor = new Color3(0.3, 0.3, 0.3);
    material.emissiveColor = new Color3(0.1, 0.1, 0.1);
    material.alpha = 0.5;
    material.backFaceCulling = false;
    // Make it not receive shadows and be slightly transparent
    material.wireframe = true;

    this.groundPlane.material = material;
    this.groundPlane.isPickable = false; // Don't interfere with other picking
    this.groundPlane.renderingGroupId = 0;

    // Add grid lines
    this.createGroundGrid(size, gridDivisions);

    logger.debug('Ground plane created');
  }

  /**
   * Create grid lines on the ground
   */
  private createGroundGrid(size: number, divisions: number): void {
    const lines: Vector3[][] = [];
    const step = size / divisions;
    const halfSize = size / 2;

    // Create lines along X axis
    for (let i = 0; i <= divisions; i++) {
      const z = -halfSize + i * step;
      lines.push([
        new Vector3(-halfSize, 0.001, z),
        new Vector3(halfSize, 0.001, z),
      ]);
    }

    // Create lines along Z axis
    for (let i = 0; i <= divisions; i++) {
      const x = -halfSize + i * step;
      lines.push([
        new Vector3(x, 0.001, -halfSize),
        new Vector3(x, 0.001, halfSize),
      ]);
    }

    const gridLines = MeshBuilder.CreateLineSystem('groundGrid', {
      lines: lines,
    }, this.scene);

    gridLines.color = new Color3(0.4, 0.4, 0.4);
    gridLines.alpha = 0.6;
    gridLines.isPickable = false;

    // Parent to ground plane so it moves together
    if (this.groundPlane) {
      gridLines.parent = this.groundPlane;
    }
  }

  /**
   * Hide the ground plane
   */
  hideGroundPlane(): void {
    if (this.groundPlane) {
      this.groundPlane.setEnabled(false);
    }
  }

  /**
   * Check if ground plane is visible
   */
  isGroundPlaneVisible(): boolean {
    return this.groundPlane?.isEnabled() ?? false;
  }

  // =========================================================================
  // SKELETON VIEWER
  // =========================================================================

  /**
   * Show skeleton for a model
   */
  showSkeleton(modelId: string, skeleton: Skeleton, mesh: AbstractMesh): void {
    // Remove existing viewer for this model if any
    this.hideSkeleton(modelId);

    const viewer = new SkeletonViewer(
      skeleton,
      mesh,
      this.scene,
      false, // autoUpdateBonesMatrices
      mesh.renderingGroupId, // renderingGroupId
      {
        pauseAnimations: false,
        returnToRest: false,
        computeBonesUsingShaders: true,
        displayMode: SkeletonViewer.DISPLAY_SPHERE_AND_SPURS,
        displayOptions: {
          sphereBaseSize: 0.01,
          sphereFactor: 0.9,
          sphereScaleUnit: 1,
          midStep: 0.25,
          midStepFactor: 0.05,
        },
      }
    );

    this.skeletonViewers.set(modelId, viewer);
    viewer.isEnabled = true;

    logger.debug('Skeleton viewer shown for:', modelId);
  }

  /**
   * Hide skeleton for a model
   */
  hideSkeleton(modelId: string): void {
    const viewer = this.skeletonViewers.get(modelId);
    if (viewer) {
      viewer.dispose();
      this.skeletonViewers.delete(modelId);
      logger.debug('Skeleton viewer hidden for:', modelId);
    }
  }

  /**
   * Check if skeleton is visible for a model
   */
  isSkeletonVisible(modelId: string): boolean {
    return this.skeletonViewers.has(modelId);
  }

  // =========================================================================
  // MESH INSPECTION
  // =========================================================================

  /**
   * Get list of meshes in a model
   */
  getMeshList(rootNode: TransformNode): MeshInfo[] {
    const meshInfos: MeshInfo[] = [];

    const collectMeshes = (node: TransformNode | AbstractMesh) => {
      if (node instanceof AbstractMesh && !(node as Mesh).isAnInstance) {
        const mesh = node as Mesh;
        const vertexData = mesh.getVerticesData ? mesh.getVerticesData('position') : null;
        const vertexCount = vertexData ? vertexData.length / 3 : 0;
        const indices = mesh.getIndices ? mesh.getIndices() : null;
        const faceCount = indices ? indices.length / 3 : 0;

        meshInfos.push({
          name: mesh.name,
          id: mesh.id,
          vertexCount,
          faceCount,
          isVisible: mesh.isVisible && mesh.isEnabled(),
          hasSkeleton: !!mesh.skeleton,
          materialName: mesh.material?.name || null,
        });
      }

      // Recurse into children
      node.getChildren().forEach((child) => {
        if (child instanceof TransformNode || child instanceof AbstractMesh) {
          collectMeshes(child);
        }
      });
    };

    collectMeshes(rootNode);
    return meshInfos;
  }

  /**
   * Set visibility for a specific mesh by name
   */
  setMeshVisibility(rootNode: TransformNode, meshName: string, visible: boolean): void {
    const findAndSetVisibility = (node: TransformNode | AbstractMesh) => {
      if (node instanceof AbstractMesh && node.name === meshName) {
        node.setEnabled(visible);
        node.isVisible = visible;
        logger.debug(`Set mesh "${meshName}" visibility to ${visible}`);
        return true;
      }

      for (const child of node.getChildren()) {
        if (child instanceof TransformNode || child instanceof AbstractMesh) {
          if (findAndSetVisibility(child)) return true;
        }
      }
      return false;
    };

    findAndSetVisibility(rootNode);
  }

  // =========================================================================
  // MODEL STATISTICS
  // =========================================================================

  /**
   * Get statistics for a model
   */
  getModelStatistics(_rootNode: TransformNode, meshes: AbstractMesh[]): ModelStats {
    let totalVertices = 0;
    let totalFaces = 0;
    let hasSkeleton = false;
    let boneCount = 0;
    let skeleton: Skeleton | null = null;

    // Calculate totals
    meshes.forEach((mesh) => {
      if (mesh instanceof Mesh) {
        const vertexData = mesh.getVerticesData('position');
        if (vertexData) {
          totalVertices += vertexData.length / 3;
        }
        const indices = mesh.getIndices();
        if (indices) {
          totalFaces += indices.length / 3;
        }
        if (mesh.skeleton && !skeleton) {
          skeleton = mesh.skeleton;
          hasSkeleton = true;
          boneCount = mesh.skeleton.bones.length;
        }
      }
    });

    // Calculate bounding box
    let min = new Vector3(Infinity, Infinity, Infinity);
    let max = new Vector3(-Infinity, -Infinity, -Infinity);

    meshes.forEach((mesh) => {
      const boundingInfo = mesh.getBoundingInfo();
      const meshMin = boundingInfo.boundingBox.minimumWorld;
      const meshMax = boundingInfo.boundingBox.maximumWorld;
      min = Vector3.Minimize(min, meshMin);
      max = Vector3.Maximize(max, meshMax);
    });

    const size = max.subtract(min);

    // Count animations
    const animationCount = this.scene.animationGroups.filter((ag) =>
      ag.targetedAnimations.some((ta) => {
        const target = ta.target;
        return meshes.includes(target as AbstractMesh) ||
          (skeleton && skeleton.bones.includes(target));
      })
    ).length;

    return {
      totalMeshes: meshes.length,
      totalVertices,
      totalFaces,
      hasSkeleton,
      boneCount,
      animationCount,
      boundingBoxSize: {
        x: isFinite(size.x) ? size.x : 0,
        y: isFinite(size.y) ? size.y : 0,
        z: isFinite(size.z) ? size.z : 0,
      },
    };
  }

  // =========================================================================
  // ANIMATION CONTROLS
  // =========================================================================

  /**
   * Get list of animations for a model
   */
  getAnimationList(meshes: AbstractMesh[]): AnimationInfo[] {
    const animations: AnimationInfo[] = [];

    // Find animation groups that target this model's meshes or skeleton
    const modelSkeletons = meshes
      .filter((m) => m.skeleton)
      .map((m) => m.skeleton!);

    this.scene.animationGroups.forEach((ag) => {
      // Check if this animation group targets any of our meshes or skeletons
      const targetsModel = ag.targetedAnimations.some((ta) => {
        const target = ta.target;
        return meshes.includes(target as AbstractMesh) ||
          modelSkeletons.some((sk) => sk.bones.includes(target));
      });

      if (targetsModel) {
        const targetNames = ag.targetedAnimations.map((ta) => {
          if (ta.target && typeof ta.target.name === 'string') {
            return ta.target.name;
          }
          return 'Unknown';
        });

        animations.push({
          name: ag.name,
          frameCount: Math.round(ag.to - ag.from),
          duration: (ag.to - ag.from) / 60, // Assuming 60 fps
          from: ag.from,
          to: ag.to,
          targetedAnimations: targetNames,
        });
      }
    });

    return animations;
  }

  /**
   * Play an animation by name
   */
  playAnimation(animationName: string, loop: boolean = true): void {
    // Stop current animation
    this.stopAnimation();

    // Find the animation group
    const animGroup = this.scene.animationGroups.find((ag) => ag.name === animationName);
    if (!animGroup) {
      logger.warn('Animation not found:', animationName);
      return;
    }

    this.currentAnimationGroup = animGroup;
    this.animationLoop = loop;

    animGroup.loopAnimation = loop;
    animGroup.speedRatio = this.animationSpeed;
    animGroup.play(loop);

    // Start frame update tracking
    this.startFrameUpdateTracking();

    logger.debug('Playing animation:', animationName);
  }

  /**
   * Pause the current animation
   */
  pauseAnimation(): void {
    if (this.currentAnimationGroup) {
      this.currentAnimationGroup.pause();
      this.stopFrameUpdateTracking();
      logger.debug('Animation paused');
    }
  }

  /**
   * Resume the paused animation
   */
  resumeAnimation(): void {
    if (this.currentAnimationGroup) {
      this.currentAnimationGroup.play(this.animationLoop);
      this.startFrameUpdateTracking();
      logger.debug('Animation resumed');
    }
  }

  /**
   * Stop the current animation
   */
  stopAnimation(): void {
    if (this.currentAnimationGroup) {
      this.currentAnimationGroup.stop();
      this.currentAnimationGroup.reset();
      this.currentAnimationGroup = null;
    }
    this.stopFrameUpdateTracking();
    logger.debug('Animation stopped');
  }

  /**
   * Set animation playback speed
   */
  setAnimationSpeed(speed: number): void {
    this.animationSpeed = speed;
    if (this.currentAnimationGroup) {
      this.currentAnimationGroup.speedRatio = speed;
    }
  }

  /**
   * Seek to a specific frame in the animation
   */
  seekAnimation(frame: number): void {
    if (!this.currentAnimationGroup) return;

    const wasPlaying = this.currentAnimationGroup.isPlaying;

    // Pause to seek
    this.currentAnimationGroup.pause();

    // Seek all animated objects to the target frame
    this.currentAnimationGroup.goToFrame(frame);

    // Resume if it was playing
    if (wasPlaying) {
      this.currentAnimationGroup.play(this.animationLoop);
    }

    logger.debug('Seeked to frame:', frame);
  }

  /**
   * Get current animation state
   */
  getCurrentAnimationState(): AnimationState | null {
    if (!this.currentAnimationGroup) {
      return null;
    }

    // Get current frame from the first animated object
    let currentFrame = 0;
    if (this.currentAnimationGroup.targetedAnimations.length > 0) {
      const firstAnim = this.currentAnimationGroup.targetedAnimations[0];
      if (firstAnim.animation && this.currentAnimationGroup.animatables.length > 0) {
        const animatable = this.currentAnimationGroup.animatables[0];
        currentFrame = animatable.masterFrame || 0;
      }
    }

    return {
      isPlaying: this.currentAnimationGroup.isPlaying,
      currentAnimation: this.currentAnimationGroup.name,
      currentFrame: Math.round(currentFrame),
      totalFrames: Math.round(this.currentAnimationGroup.to - this.currentAnimationGroup.from),
      speed: this.animationSpeed,
      loop: this.animationLoop,
    };
  }

  /**
   * Start tracking frame updates for UI
   */
  private startFrameUpdateTracking(): void {
    this.stopFrameUpdateTracking();

    this.frameUpdateInterval = setInterval(() => {
      if (this.currentAnimationGroup && this.onAnimationFrameUpdate) {
        const state = this.getCurrentAnimationState();
        if (state) {
          this.onAnimationFrameUpdate(state.currentFrame, state.totalFrames);
        }
      }
    }, 50); // Update ~20 times per second
  }

  /**
   * Stop tracking frame updates
   */
  private stopFrameUpdateTracking(): void {
    if (this.frameUpdateInterval) {
      clearInterval(this.frameUpdateInterval);
      this.frameUpdateInterval = null;
    }
  }

  // =========================================================================
  // CLEANUP
  // =========================================================================

  /**
   * Dispose all resources
   */
  dispose(): void {
    // Stop animation tracking
    this.stopFrameUpdateTracking();
    this.stopAnimation();

    // Dispose individual gizmos
    if (this.positionGizmo) {
      this.positionGizmo.dispose();
      this.positionGizmo = null;
    }
    if (this.rotationGizmo) {
      this.rotationGizmo.dispose();
      this.rotationGizmo = null;
    }
    if (this.scaleGizmo) {
      this.scaleGizmo.dispose();
      this.scaleGizmo = null;
    }

    // Dispose utility layer
    if (this.utilityLayer) {
      this.utilityLayer.dispose();
      this.utilityLayer = null;
    }

    // Dispose skeleton viewers
    this.skeletonViewers.forEach((viewer) => viewer.dispose());
    this.skeletonViewers.clear();

    // Dispose human scale reference
    if (this.humanScaleRef) {
      this.humanScaleRef.dispose();
      this.humanScaleRef = null;
    }

    this.attachedNode = null;
    this.currentAnimationGroup = null;

    logger.debug('ModelEditorComponent disposed');
  }
}
